import {
  COOKIES_CONFIG,
  updateUserProperties,
  storeSessionContext,
} from '@belong/common';
import { datadogRum } from '@datadog/browser-rum';
import { setEnhancedConvertionData } from 'analytics/ga.utils';
import { isNil } from 'es-toolkit/compat';
import { resetAppModal } from 'lease-signing-flow-v2/store/app';
import { PaymentMethod, VerificationMethod } from 'models/enums';
import { parseCookies, setCookie, destroyCookie } from 'nookies';
import { fetchFavoriteHomes } from 'store/redux/renter-accounts/actions';
import { getMediaTypeFromFile } from 'utils/file';
import { uploadDocumentToS3 } from '../common/uploadDocumentToS3';
import { getApiActionTypesValues } from '../redux_utils';
import { fetchRegions } from '../settings';
import { ACTIONS } from './constants';

const {
  HAS_ACCOUNT,
  JWT_TOKEN,
  REFRESH_TOKEN,
  REFERRAL_ID,
  REFERRAL_USER,
  PARTNER_KEY,
} = COOKIES_CONFIG;

export const fetchUserClaims = () => ({
  types: getApiActionTypesValues('FETCH_USER_CLAIMS'),
  promise: ({ client }) => client.get('/users/claims'),
  auth: true,
  redirectToHomeOnHide: true,
});

const setUserSession = (data) => ({
  type: ACTIONS.USER_SET_SESSION,
  result: data,
});

export const sessionRegister = (user) => async (dispatch) => {
  const data = await dispatch({
    types: getApiActionTypesValues('USER_SESSION_REGISTER'),
    promise: async ({ client, octaneClient }) => {
      const cookies = parseCookies();
      const partnerKey = cookies[PARTNER_KEY.name];
      const response = await client.post('/users/register', {
        ...user,
        encodedPartnerKey: partnerKey,
      });

      setEnhancedConvertionData(user);

      client.setJwtToken(response.token);
      octaneClient.setJwtToken(response.token);
      dispatch(setUserSession({ user }));
      return response;
    },
  });

  return data;
};

export const register = (user) => async (dispatch) => {
  const data = await dispatch({
    types: [
      ACTIONS.USER_REGISTER_REQUEST,
      ACTIONS.USER_REGISTER_SUCCESS,
      ACTIONS.USER_REGISTER_FAILURE,
    ],
    promise: ({ client }) => {
      const cookies = parseCookies();
      const partnerKey = cookies[PARTNER_KEY.name];
      return client.post('/users/register', {
        ...user,
        encodedPartnerKey: partnerKey,
      });
    },
  });

  // Store registration data in Mixpanel (user will be identified later via fetchUser)
  if (user?.email && window.mixpanel) {
    window.mixpanel.register({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone,
      registrationSource: 'belong-web',
      registrationTimestamp: new Date().toISOString(),
    });
  }

  return data;
};

export const login = (user) => async (dispatch) => {
  const data = await dispatch({
    promise: async ({ client, octaneClient }) => {
      const response = await client.post('/users/login', { ...user });

      setEnhancedConvertionData(user);

      client.setJwtToken(response.token);
      octaneClient.setJwtToken(response.token);
      setCookie(null, JWT_TOKEN.name, response.token, JWT_TOKEN.options);
      setCookie(
        null,
        REFRESH_TOKEN.name,
        response.refreshToken,
        REFRESH_TOKEN.options,
      );

      // Store login data in Mixpanel (user will be identified later via fetchUser)
      if (user?.email && window.mixpanel) {
        window.mixpanel.register({
          email: user.email,
          loginSource: 'belong-web',
          loginTimestamp: new Date().toISOString(),
        });
      }

      return response;
    },
  });

  return data;
};

export const loginExternal = (loginObject) => async (dispatch) => {
  const data = await dispatch({
    promise: async ({ client, octaneClient }) => {
      const response = await client.post('/users/login-external', {
        ...loginObject,
      });

      setEnhancedConvertionData(loginObject);

      client.setJwtToken(response.token);
      octaneClient.setJwtToken(response.token);
      setCookie(null, JWT_TOKEN.name, response.token, JWT_TOKEN.options);
      setCookie(
        null,
        REFRESH_TOKEN.name,
        response.refreshToken,
        REFRESH_TOKEN.options,
      );

      // Store external login data in Mixpanel (user will be identified later via fetchUser)
      if (loginObject?.email && window.mixpanel) {
        window.mixpanel.register({
          email: loginObject.email,
          loginSource: 'belong-web-external',
          loginTimestamp: new Date().toISOString(),
        });
      }

      return response;
    },
  });

  return data;
};

export const refreshToken = (refreshTokenData) => ({
  types: getApiActionTypesValues('REFRESH_TOKEN'),
  promise: ({ client }) => client.post('/users/refreshToken', refreshTokenData),
});

export const fetchUser = () => async (dispatch) => {
  const userObject = await dispatch({
    types: [
      ACTIONS.USER_FETCH_REQUEST,
      ACTIONS.USER_FETCH_SUCCESS,
      ACTIONS.USER_FETCH_FAILURE,
    ],
    promise: ({ client }) => client.get('/users'),
  });

  const accessibilityPreferences = {
    themePreference: window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light',
    languagePreference: window.navigator.language,
    reducedMotionPreference: window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches,
    isHighContrastModePreference: window.matchMedia('(forced-colors: active)')
      .matches,
  };

  setCookie(null, HAS_ACCOUNT.name, true, HAS_ACCOUNT.options);

  if (userObject?.userId) {
    // Identify user with their actual userId
    if (window.mixpanel) {
      window.mixpanel?.identify?.(userObject.userId);
    }

    updateUserProperties({
      email: userObject.email,
      firstName: userObject.firstName,
      lastName: userObject.lastName,
      phone: userObject.phone,
      userType: userObject.userType || 'homeowner',
      address_street: userObject.address?.streetAddress,
      address_city: userObject.address?.city,
      address_state: userObject.address?.state,
      address_zipcode: userObject.address?.zipcode,
      address_country: userObject.address?.country,
      preferences: userObject.preferences
        ? JSON.stringify(userObject.preferences)
        : undefined,
      ...accessibilityPreferences,
    });

    storeSessionContext(accessibilityPreferences);

    datadogRum.setUser({
      id: userObject?.userId,
      email: userObject.email,
      ...accessibilityPreferences,
    });

    window?.cioanalytics?.identify(userObject?.userId, {
      email: userObject.email,
    });
  }

  // Best-effort fanout: a claims/favorites/regions failure must not reject the
  // user fetch (each dispatch tracks its own failure via its own action types).
  // Callers rely on `userObject` whenever `/users` itself succeeded — e.g. the
  // agreement flow's set-password modal builds its `register` body from it, so
  // dropping it here would submit without the freshly collected email/phone.
  await Promise.allSettled([
    dispatch(fetchUserClaims()),
    dispatch(fetchFavoriteHomes()),
    dispatch(fetchRegions()),
  ]);

  return userObject;
};

// PUT : /users/phones
export const updateUserPhone = (userPhoneObject) => ({
  types: getApiActionTypesValues('UPDATE_USER_PHONE'),
  promise: ({ client }) => client.put('/users/phones', userPhoneObject),
});

export const forgotPassword = (userEmail) => ({
  types: [
    ACTIONS.USER_FORGOT_PASSWORD_REQUEST,
    ACTIONS.USER_FORGOT_PASSWORD_SUCCESS,
    ACTIONS.USER_FORGOT_PASSWORD_FAILURE,
  ],
  promise: ({ client }) =>
    client.post('/users/forgot-password', { ...userEmail }),
});

export const resetPassword = (resetPasswordObject) => ({
  types: [
    ACTIONS.USER_RESET_PASSWORD_REQUEST,
    ACTIONS.USER_RESET_PASSWORD_SUCCESS,
    ACTIONS.USER_RESET_PASSWORD_FAILURE,
  ],
  promise: ({ client }) =>
    client.post('/users/reset-password', { ...resetPasswordObject }),
});

export const setPassword = (setPasswordObject) => ({
  types: [
    ACTIONS.USER_SET_PASSWORD_REQUEST,
    ACTIONS.USER_SET_PASSWORD_SUCCESS,
    ACTIONS.USER_SET_PASSWORD_FAILURE,
  ],
  promise: ({ client }) =>
    client.post('/users/set-password', { ...setPasswordObject }),
});

export const fetchUserToDos = () => ({
  types: getApiActionTypesValues('FETCH_USER_TODOS'),
  promise: ({ client }) => client.get('/users/todo'),
});

export const fetchDwollaToken = (paymentMethod) => ({
  types: getApiActionTypesValues('USER_FETCH_DWOLLA_TOKEN'),
  promise: ({ client }) =>
    client.post('/users/payment-methods/token', {
      paymentMethod,
    }),
  auth: true,
});

export const fetchPlaidToken = (products, redirectUrl) => ({
  types: getApiActionTypesValues('USER_FETCH_PLAID_TOKEN'),
  promise: ({ client }) =>
    client.post('/users/payment-methods/link-token', {
      redirectUrl,
      products,
    }),
  auth: true,
});

// Note: this endpoint returns a sorted array of payment methods.
// The sorting is based on the last-added-first order.
// Eg: response[0] is the last added payment account (ach/cc) for this user
export const fetchPaymentMethods = () => ({
  types: getApiActionTypesValues('USER_FETCH_PAYMENT_METHODS'),
  promise: ({ client }) => client.get('/users/payment-methods'),
  auth: true,
});

export const addAchInstantPaymentMethod =
  (token, metadata, defaultCard, doNotFetchPaymentMethodsOnSuccess) =>
  async (dispatch) => {
    const [lastAddedPaymentMethod] = await dispatch({
      types: getApiActionTypesValues('USER_ADD_PAYMENT_METHOD'),
      promise: ({ client }) =>
        client.post('/users/payment-methods', {
          paymentMethod: PaymentMethod.Ach,
          setAsDefault: defaultCard,
          achAccountInfo: {
            verificationMethod: VerificationMethod.Instant,
            instantAchAccount: {
              accountToken: token,
              verifiedAccountData: metadata,
            },
          },
        }),
      auth: true,
    });

    if (doNotFetchPaymentMethodsOnSuccess) {
      return lastAddedPaymentMethod;
    }

    await dispatch(fetchPaymentMethods());
    return lastAddedPaymentMethod;
  };

export const createAchVerificationSession = (body = {}) => ({
  types: getApiActionTypesValues('USER_CREATE_ACH_VERIFICATION_SESSION'),
  promise: ({ client }) =>
    client.post('/users/payment-methods/ach/verification-sessions', body),
  auth: true,
});

export const createAchUpdateLinkToken = (sessionId, body = {}) => ({
  types: getApiActionTypesValues('USER_CREATE_ACH_UPDATE_LINK_TOKEN'),
  promise: ({ client }) =>
    client.post(
      `/users/payment-methods/ach/verification-sessions/${sessionId}/update-link-token`,
      body,
    ),
  auth: true,
});

export const completeAchVerificationSession = (sessionId, body) => ({
  types: getApiActionTypesValues('USER_COMPLETE_ACH_VERIFICATION_SESSION'),
  promise: ({ client }) =>
    client.post(
      `/users/payment-methods/ach/verification-sessions/${sessionId}/complete`,
      body,
    ),
  auth: true,
});

export const fetchAchBankAccountSetups = () => ({
  types: getApiActionTypesValues('USER_FETCH_ACH_BANK_ACCOUNT_SETUPS'),
  promise: ({ client }) =>
    client.get('/users/payment-methods/ach/bank-account-setups'),
  auth: true,
});

export const checkAchBankAccountSetupStatus = (sessionId) => ({
  types: getApiActionTypesValues('USER_CHECK_ACH_BANK_ACCOUNT_SETUP'),
  promise: ({ client }) =>
    client.post(
      `/users/payment-methods/ach/bank-account-setups/${sessionId}/check-status`,
    ),
  auth: true,
});

export const addCreditCardPaymentMethod =
  (creditCardDetails) => async (dispatch) => {
    const addedCard = await dispatch({
      types: getApiActionTypesValues('USER_ADD_PAYMENT_METHOD_CC'),
      promise: ({ client }) =>
        client.post('/users/payment-methods', {
          paymentMethod: PaymentMethod.CreditCard,
          creditCardInfo: {
            cardNumber: creditCardDetails.creditCardNumber,
            expirationMonth: Number(
              creditCardDetails.expirationDate.split('/')[0],
            ),
            expirationYear: Number(
              creditCardDetails.expirationDate.split('/')[1],
            ),
            cvc: creditCardDetails.cvc,
            zipcode: creditCardDetails.billingZipCode,
          },
        }),
      auth: true,
    });
    await dispatch(fetchPaymentMethods());
    return addedCard;
  };

export const addAchMicrodepositPaymentMethod =
  (metadata) => async (dispatch) => {
    await dispatch({
      types: getApiActionTypesValues('USER_ADD_PAYMENT_METHOD'),
      promise: ({ client }) =>
        client.post('/users/payment-methods', {
          paymentMethod: PaymentMethod.Ach,
          achAccountInfo: {
            verificationMethod: VerificationMethod.MicroDeposit,
            manualAchAccount: metadata,
          },
        }),
      auth: true,
    });

    const addedPaymentAccounts = await dispatch(fetchPaymentMethods());
    return addedPaymentAccounts;
  };

export const verifyAchMicrodepositPaymentMethod =
  (accountId, deposits) => async (dispatch) => {
    await dispatch({
      types: getApiActionTypesValues('USER_VERIFY_PAYMENT_METHOD'),
      promise: ({ client }) =>
        client.put(`/users/payment-methods/${accountId}/ach-verify`, {
          ...deposits,
        }),
      auth: true,
    });

    const paymentMethods = await dispatch(fetchPaymentMethods());
    return paymentMethods;
  };

export const updatePaymentMethod =
  (id, paymentType, priority) => async (dispatch) => {
    await dispatch({
      types: getApiActionTypesValues('UPDATE_DEFAULT_PAYMENT'),
      promise: ({ client }) =>
        client.put(`/users/payment-methods/${id}`, {
          paymentType,
          priority,
        }),
      auth: true,
    });

    await Promise.all([
      dispatch(fetchPaymentMethods()),
      dispatch(fetchUserToDos()),
    ]);
  };

export const updateLegalIdentityInformation =
  (legalIdentityInformation) => (dispatch) =>
    dispatch({
      types: getApiActionTypesValues('UPDATE_USER_LEGAL_IDENTITY_INFO'),
      promise: ({ client }) =>
        client.put('/users/legal-identity', legalIdentityInformation),
      auth: true,
    });

export const fetchUserVerifications = (userId) => (dispatch) =>
  dispatch({
    types: getApiActionTypesValues('FETCH_USER_VERIFICATIONS'),
    promise: ({ octaneClient }) =>
      octaneClient
        .get(`/v1/verification?userId=${userId}`)
        .then(([userVerifications]) => userVerifications?.verifications || []),
    auth: true,
  });

export const fetchUserIdentityVerification = (verificationId) => (dispatch) =>
  dispatch({
    types: getApiActionTypesValues('FETCH_USER_IDENTITY_VERIFICATION'),
    promise: ({ octaneClient }) =>
      octaneClient.get(`/v1/verification/identity/${verificationId}`),
    auth: true,
  });

export const createUserIdentityVerification = (userId) => (dispatch) =>
  dispatch({
    types: getApiActionTypesValues('CREATE_USER_IDENTITY_VERIFICATION'),
    promise: ({ octaneClient }) =>
      octaneClient.post(`/v1/verification/identity/${userId}`),
    auth: true,
  });

// POST : /users/verifications/id/run

export const fetchResidentLeases = () => (dispatch) =>
  dispatch({
    types: getApiActionTypesValues('FETCH_LEASE'),
    promise: ({ client }) => client.get('/residents/summary'),
    auth: true,
  });

export const fetchUserDetailsById = (id) => (dispatch) =>
  dispatch({
    types: getApiActionTypesValues('FETCH_USER_DETAILS_BY_ID'),
    promise: ({ client }) => client.get(`/users/profile/${id}`),
    auth: true,
  });

export const fetchCurrentUserDetails = () => async (dispatch) => {
  const userProfile = await dispatch({
    types: getApiActionTypesValues('FETCH_CURRENT_USER_DETAILS'),
    promise: ({ client }) => client.get('/users/profile'),
  });

  setEnhancedConvertionData(userProfile);

  await dispatch(fetchUserClaims());

  return userProfile;
};

const getDocumentUploadUrl = () => ({
  promise: ({ client }) => client.get('/users/profile-image/upload-url'),
  auth: true,
});

const createProfileImage = (uniqueId) => {
  if (uniqueId) {
    return {
      promise: ({ client }) => client.post('/users/profile-image', uniqueId),
      auth: true,
    };
  }
};

const deleteProfileImage = () => ({
  promise: ({ client }) => client.delete('/users/profile-image'),
  auth: true,
});

export const uploadProfilePhoto =
  ({ stagingMedia, deletedMedia }) =>
  async (dispatch) => {
    if (isNil(stagingMedia) && isNil(deletedMedia)) return null;
    if (deletedMedia.length) await dispatch(deleteProfileImage());
    if (stagingMedia.length) {
      const file = stagingMedia[0];
      const mediaType = await getMediaTypeFromFile(file);
      const response = mediaType
        ? await dispatch(getDocumentUploadUrl(mediaType))
        : null;
      const { headers, uploadUrl, uniqueId } = response;
      await uploadDocumentToS3(dispatch, file, headers, uploadUrl);
      await dispatch(createProfileImage({ mediaId: uniqueId }));
      return uniqueId;
    }
  };

const userDataClear = () => ({
  type: ACTIONS.USER_DATA_CLEAR,
});

const createUserLogoutBackend = () => ({
  promise: ({ client }) => client.post('/users/logout'),
});

export const logOut = () => async (dispatch) => {
  await dispatch(createUserLogoutBackend());
  await dispatch(resetAppModal());
  await dispatch({
    promise: ({ client, octaneClient }) => {
      client.clearJwtToken();
      octaneClient.clearJwtToken();
      destroyCookie(null, JWT_TOKEN.name);
      destroyCookie(null, REFRESH_TOKEN.name);
      destroyCookie(null, REFERRAL_USER.name, { path: '/' });
      destroyCookie(null, REFERRAL_ID.name, { path: '/' });
      window.Intercom?.('shutdown');
      dispatch(userDataClear());
      return Promise.resolve();
    },
  });
};

export const validateUserInterestSurveyEntry = (interestUpdateToken) => ({
  promise: ({ client }) =>
    client.head(`/residents/leads/interest/${interestUpdateToken}/valid`),
});

export const fetchReports = () => ({
  types: getApiActionTypesValues('FETCH_REPORTS'),
  promise: ({ client }) => client.get('/homeowners/accounts/reports'),
  auth: true,
});

export const fetchUserSetPasswordStatus = (uniqueId, setToken = true) => ({
  promise: async ({ client }) => {
    const response = await client.get(`/users/login-status/${uniqueId}`);
    if (setToken && response.passwordSet === false) {
      client.setJwtToken(response?.token?.token);

      setCookie(
        null,
        JWT_TOKEN.name,
        response?.token?.token,
        JWT_TOKEN.options,
      );
      setCookie(
        null,
        REFRESH_TOKEN.name,
        response?.token?.refreshToken,
        REFRESH_TOKEN.options,
      );
    }
    return response;
  },
});

export const privateFileDownload = (fileType, ownerId) => ({
  promise: async ({ client }) => {
    let url;
    if (fileType === 'adverse-action-document') {
      const response = await client({
        method: 'get',
        url: `/residents/applications/${fileType}/${ownerId}`,
        responseType: 'arraybuffer',
      });

      const blob = new Blob([response], { type: 'application/pdf' });
      url = URL.createObjectURL(blob);
    }
    return url;
  },
  auth: true,
});

export const fetchAllFlows = () => ({
  types: getApiActionTypesValues('FETCH_FLOWS'),
  promise: ({ client }) => client.get('/homeowners/flows'),
  auth: true,
});

export const getUserDevices = () => ({
  promise: ({ client }) => client.get('/users/devices'),
  auth: true,
});

export const sendTextMessage = (message) => ({
  promise: ({ client }) => client.post('/users/text-message', message),
  auth: true,
});

export const fetchUserAutoSplitPaymentMethod = () => ({
  types: getApiActionTypesValues('FETCH_USER_AUTO_SPLIT_PAYMENT_METHOD'),
  promise: ({ client }) => client.get('/users/payment-methods/auto-split'),
  auth: true,
});

export const updateUserAutoSplitPaymentMethod =
  (isEnabled) => async (dispatch) => {
    await dispatch({
      types: getApiActionTypesValues('UPDATE_USER_AUTO_SPLIT_PAYMENT_METHOD'),
      promise: ({ client }) =>
        client.put('/users/payment-methods/auto-split', {
          autoSplitEnabled: isEnabled,
        }),
      auth: true,
    });

    await Promise.all([
      dispatch(fetchUserAutoSplitPaymentMethod()),
      dispatch(fetchPaymentMethods()),
    ]);
  };

export const fetchUserPaymentMethodConfigurations = () => ({
  types: getApiActionTypesValues('FETCH_USER_PAYMENT_METHOD_CONFIGURATIONS'),
  promise: ({ client }) => client.get('/users/payment-methods/payment-user'),
  auth: true,
});
